import React, { useMemo } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Legend
} from 'recharts';
import {
    Database,
    User,
    BarChart2,
    FileText,
    CheckCircle,
    AlertCircle,
    Clock,
    Building,
    Users,
    Quote,
    Zap,
    Tag
} from 'lucide-react';

// --- Global Search Result ---

export const GlobalSearchResult = ({ data }: { data: any }) => {
    const results = data.results || [];
    const totalFound = data.total_found || results.length;

    if (!results.length) {
        return (
            <div className="text-xs text-gray-500 italic">
                No results found for &quot;{data.query}&quot;
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between text-xs text-gray-500 border-b border-gray-100 pb-2">
                <span>Query: <span className="font-medium text-gray-700">&quot;{data.query}&quot;</span></span>
                <span>Found: {totalFound}</span>
            </div>
            <div className="grid gap-2">
                {results.map((result: any, idx: number) => (
                    <div
                        key={result.external_id || idx}
                        className="flex items-center gap-3 p-2 rounded-lg border border-gray-200 bg-white hover:border-blue-300 transition-colors"
                    >
                        <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                            {result.type === 'accounts' ? <Building size={14} /> : <Users size={14} />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-gray-900 truncate">
                                    {result.name}
                                </span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 uppercase tracking-wider">
                                    {result.type}
                                </span>
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                                ID: {result.external_id}
                            </div>
                        </div>
                        {result.confidence && (
                            <div className="text-[10px] text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
                                {(result.confidence * 100).toFixed(0)}% Match
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- Workspace Traits Result ---

export const WorkspaceTraitsResult = ({ data }: { data: any }) => {
    const traits = data.account_traits || [];
    const workspaceId = data.workspace_id;

    if (!traits.length) {
        return <div className="text-xs text-gray-500">No traits found.</div>;
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-gray-500">
                <Database size={12} />
                <span>Workspace ID: {workspaceId}</span>
            </div>
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-3 py-2 font-medium text-gray-700">Display Name</th>
                                <th className="px-3 py-2 font-medium text-gray-700">Key</th>
                                <th className="px-3 py-2 font-medium text-gray-700">Type</th>
                                <th className="px-3 py-2 font-medium text-gray-700">Source</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {traits.slice(0, 10).map((trait: any, idx: number) => (
                                <tr key={trait.name || idx} className="hover:bg-gray-50/50">
                                    <td className="px-3 py-2 font-medium text-gray-900">
                                        {trait.display_name}
                                    </td>
                                    <td className="px-3 py-2 text-gray-500 font-mono text-[10px]">
                                        {trait.name}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600">
                                        <span className="px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200">
                                            {trait.data_type}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 capitalize">
                                        {trait.source}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {traits.length > 10 && (
                    <div className="px-3 py-2 bg-gray-50 text-[10px] text-gray-500 text-center border-t border-gray-200">
                        Showing 10 of {traits.length} traits
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Entity Profile Result ---

export const EntityProfileResult = ({ data }: { data: any }) => {
    const { entity_name: entityName, entity_type: entityType, external_id: externalId, traits } = data;

    // Filter out null/empty traits for cleaner display
    const displayTraits = Object.entries(traits || {})
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .slice(0, 8); // Show top 8 traits

    return (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-blue-100">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-white border border-blue-100 flex items-center justify-center text-blue-600 shadow-sm">
                            {entityType === 'accounts' ? <Building size={18} /> : <User size={18} />}
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-900">{entityName}</h3>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span className="capitalize">{entityType}</span>
                                <span>•</span>
                                <span className="font-mono">{externalId}</span>
                            </div>
                        </div>
                    </div>
                    {traits?.subscription_status && (
                        <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-[10px] font-medium border border-green-200">
                            {traits.subscription_status}
                        </span>
                    )}
                </div>
            </div>

            <div className="p-4">
                <h4 className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-3">
                    Key Attributes
                </h4>
                <div className="grid grid-cols-2 gap-3">
                    {displayTraits.map(([key, value]) => (
                        <div key={key} className="space-y-0.5">
                            <div className="text-[10px] text-gray-500 capitalize truncate">
                                {key.replace(/_/g, ' ')}
                            </div>
                            <div className="text-xs font-medium text-gray-900 truncate" title={String(value)}>
                                {String(value)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {traits?.last_seen && (
                <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-[10px] text-gray-500 flex items-center gap-1.5">
                    <Clock size={10} />
                    Last seen: {new Date(traits.last_seen).toLocaleString()}
                </div>
            )}
        </div>
    );
};

// --- Metrics Graph Result ---

export const MetricsGraphResult = ({ data }: { data: any }) => {
    const metrics = data.metrics || [];

    // Transform data for Recharts
    // We need to merge all metric data points into a single array keyed by timestamp
    const chartData = useMemo(() => {
        const dataMap = new Map();

        metrics.forEach((metric: any) => {
            metric.data_points.forEach((point: any) => {
                const ts = point.timestamp;
                if (!dataMap.has(ts)) {
                    dataMap.set(ts, { timestamp: ts });
                }
                const entry = dataMap.get(ts);
                entry[metric.metric_name] = point.value;
            });
        });

        return Array.from(dataMap.values()).sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
    }, [metrics]);

    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

    if (!metrics.length) {
        return <div className="text-xs text-gray-500">No metrics data available.</div>;
    }

    return (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                    <BarChart2 size={14} className="text-blue-500" />
                    Metrics Overview
                </h3>
                <div className="text-[10px] text-gray-500 bg-gray-50 px-2 py-1 rounded">
                    {new Date(data.timeframe?.from_date).toLocaleDateString()} - {new Date(data.timeframe?.to_date).toLocaleDateString()}
                </div>
            </div>

            <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                        <XAxis
                            dataKey="timestamp"
                            tick={{ fontSize: 10, fill: '#6b7280' }}
                            tickFormatter={(val) => new Date(val).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            tick={{ fontSize: 10, fill: '#6b7280' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <RechartsTooltip
                            contentStyle={{
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                                fontSize: '11px'
                            }}
                            labelFormatter={(label) => new Date(label).toLocaleDateString()}
                        />
                        <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                        {metrics.map((metric: any, idx: number) => (
                            <Line
                                key={metric.metric_name}
                                type="monotone"
                                dataKey={metric.metric_name}
                                name={metric.metric_name.replace(/_/g, ' ').replace('default metric', '').trim()}
                                stroke={colors[idx % colors.length]}
                                strokeWidth={2}
                                dot={{ r: 2, strokeWidth: 0 }}
                                activeDot={{ r: 4 }}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

// --- Qualitative Extraction Result ---

export const QualitativeExtractionResult = ({ data }: { data: any }) => {
    const results = data.results || [];
    const totalCount = data.total_count || results.length;

    if (!results.length) {
        return <div className="text-xs text-gray-500">No qualitative data found.</div>;
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                    <FileText size={12} />
                    Extraction Results
                </span>
                <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                    {totalCount} Found
                </span>
            </div>

            <div className="space-y-3">
                {results.map((result: any, idx: number) => (
                    <div key={result.extraction_id || idx} className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                        <div className="flex items-start gap-3 mb-2">
                            <div className="mt-0.5">
                                {result.extraction_status === 'completed' ? (
                                    <CheckCircle size={14} className="text-green-500" />
                                ) : (
                                    <AlertCircle size={14} className="text-amber-500" />
                                )}
                            </div>
                            <div className="flex-1">
                                <p className="text-xs text-gray-800 leading-relaxed">
                                    {result.summary_gist}
                                </p>
                            </div>
                        </div>

                        {result.themes && result.themes.length > 0 && (
                            <div className="ml-7 flex flex-wrap gap-1.5 mt-2">
                                {result.themes.map((theme: string) => (
                                    <span
                                        key={theme}
                                        className="px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-[10px] text-gray-600"
                                    >
                                        #{theme}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="ml-7 mt-2 flex items-center gap-3 text-[10px] text-gray-400">
                            <span>Concepts: {result.concept_count}</span>
                            <span>Tasks: {result.task_count}</span>
                            <span>{new Date(result.extracted_at).toLocaleDateString()}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- Qualitative Search Board Result (Unified Concepts) ---

export const QualitativeSearchBoard = ({ data }: { data: any }) => {
    // If data is still a string, try to parse it using the enhanced parseToolResult function logic
    let parsedData = data;
    if (typeof data === 'string') {
        try {
            // Try parsing as JSON first
            parsedData = JSON.parse(data);
        } catch {
            // If that fails, handle Python-style string representation
            try {
                let fixed = data;

                // Replace Python UUID() objects with string UUIDs
                // Example: UUID('4c0a81ce-7fe9-4954-9d1b-c6923a866a2e') -> "4c0a81ce-7fe9-4954-9d1b-c6923a866a2e"
                fixed = fixed.replace(/UUID\('([^']+)'\)/g, '"$1"');

                // Replace Python datetime.datetime() objects with ISO strings
                // Example: datetime.datetime(2025, 12, 18, 13, 19, 57, 783889, tzinfo=datetime.timezone.utc) -> "2025-12-18T13:19:57.783Z"
                fixed = fixed.replace(
                    /datetime\.datetime\((\d+),\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d+)[^)]*\)/g,
                    (match, year, month, day, hour, minute, second, microsecond) => {
                        const isoDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}.${String(microsecond).padStart(6, '0').slice(0, 3)}Z`;
                        return `"${isoDate}"`;
                    }
                );

                // Replace Python None, True, False
                fixed = fixed
                    .replace(/\bNone\b/g, 'null')
                    .replace(/\bTrue\b/g, 'true')
                    .replace(/\bFalse\b/g, 'false');

                // Handle quotes carefully
                // First temporarily replace escaped single quotes
                fixed = fixed.replace(/\\'/g, '___ESCAPED_QUOTE___');
                // Then replace single quotes with double quotes
                fixed = fixed.replace(/'/g, '"');
                // Restore escaped quotes as proper escaped double quotes
                fixed = fixed.replace(/___ESCAPED_QUOTE___/g, '\\"');

                parsedData = JSON.parse(fixed);
            } catch (e) {
                console.error('[QualitativeSearchBoard] Failed to parse data:', e);
                parsedData = data;
            }
        }
    }

    // Determine data source structure
    // The user provided structure has 'results': { 'documents': [], 'concepts': [] }
    // But sometimes it might be just 'concepts': [] at the top level or 'results': [] array
    const concepts = parsedData.results?.concepts || parsedData.concepts || (Array.isArray(parsedData.results) ? parsedData.results : []) || [];
    const counts = parsedData.counts || {};
    const totalCount = parsedData.total_count || concepts.length;

    // Helper to get polarity color
    const getPolarityStyle = (polarity: string) => {
        const p = (polarity || '').toLowerCase();
        if (p === 'positive') return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: 'text-emerald-500' };
        if (p === 'negative') return { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', icon: 'text-rose-500' };
        return { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', icon: 'text-slate-500' };
    };

    if (!concepts.length) {
        return <div className="text-xs text-gray-500 italic">No concepts found.</div>;
    }

    return (
        <div className="space-y-4">
            {/* Header Summary */}
            <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-100">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-md border border-gray-200 shadow-sm">
                        <Zap size={16} className="text-amber-500" />
                    </div>
                    <div>
                        <div className="text-xs font-semibold text-gray-900">Qualitative Insights</div>
                        <div className="text-[10px] text-gray-500">
                            {counts.concepts !== undefined ? `${counts.concepts} Concepts` : `${totalCount} Concepts`}
                            {counts.documents !== undefined && counts.documents > 0 && ` • ${counts.documents} Docs`}
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    {Object.entries(counts).map(([key, val]: [string, any]) => (
                        key !== 'concepts' && key !== 'documents' && (
                            <div key={key} className="text-[10px] bg-white px-2 py-1 rounded border border-gray-200 text-gray-600">
                                {key}: {val}
                            </div>
                        )
                    ))}
                </div>
            </div>

            {/* Grid of Concept Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[500px] overflow-y-auto pr-1 custom-scrollbar">
                {concepts.map((concept: any, idx: number) => {
                    const style = getPolarityStyle(concept.polarity);

                    return (
                        <div
                            key={concept.concept_key || idx}
                            className={`rounded-xl border ${style.border} bg-white shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden flex flex-col`}
                        >
                            {/* Card Header */}
                            <div className={`px-3 py-2 border-b ${style.border} ${style.bg} flex justify-between items-start`}>
                                <div className="min-w-0 flex-1 mr-2">
                                    <h3 className="text-xs font-bold text-gray-900 leading-tight truncate" title={concept.display_name}>
                                        {concept.display_name || 'Untitled Concept'}
                                    </h3>
                                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-white/60 ${style.text} border border-white/40 shadow-sm`}>
                                            {concept.polarity ? concept.polarity.charAt(0).toUpperCase() + concept.polarity.slice(1) : 'Neutral'}
                                        </span>
                                        {concept.confidence && (
                                            <span className="text-[10px] text-gray-500" title="Confidence Score">
                                                {Math.round(concept.confidence * 100)}%
                                            </span>
                                        )}
                                        {(concept.uploaded_at || concept.source_created_at) && (
                                            <>
                                                <span className="text-gray-300">•</span>
                                                <span className="text-[10px] font-medium text-gray-600">
                                                    {new Date(concept.uploaded_at || concept.source_created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {concept.concept_category && (
                                    <div className="text-[9px] uppercase tracking-wider text-gray-500 font-medium px-1.5 py-0.5 bg-white rounded border border-gray-200 opacity-80">
                                        {concept.concept_category.replace(/_/g, ' ')}
                                    </div>
                                )}
                            </div>

                            {/* Card Body */}
                            <div className="p-3 flex-1 flex flex-col gap-3">
                                {/* Summary Gist */}
                                <p className="text-xs text-gray-700 leading-relaxed font-medium">
                                    {concept.summary_gist}
                                </p>

                                {/* Evidence Text Quote */}
                                {concept.evidence_text && (
                                    <div className="relative pl-3 border-l-2 border-indigo-100 bg-slate-50 py-2 pr-2 rounded-r-md">
                                        <Quote size={10} className="absolute left-[-6px] top-[-4px] text-indigo-300 bg-white rounded-full p-0.5" fill="currentColor" />
                                        <p className="text-[11px] text-slate-600 italic leading-snug">
                                            &quot;{concept.evidence_text}&quot;
                                        </p>
                                    </div>
                                )}

                                {/* Bullets */}
                                {concept.summary_bullets && concept.summary_bullets.length > 0 && (
                                    <ul className="space-y-1">
                                        {concept.summary_bullets.slice(0, 3).map((bullet: string, bIdx: number) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <li key={`${concept.concept_key}-${bIdx}`} className="text-[10px] text-gray-600 flex items-start gap-1.5">
                                                <span className="mt-1 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                                                <span className="leading-snug">{bullet}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Card Footer */}
                            <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 flex flex-wrap gap-2 items-center justify-between">
                                <div className="flex flex-wrap gap-1">
                                    {(concept.topic_tags || []).slice(0, 3).map((tag: string) => (
                                        <span key={tag} className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white border border-gray-200 text-[9px] text-gray-500">
                                            <Tag size={8} />
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
